import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getItem, createItem, updateItem } from '../../services/ApiService';
import { Item, CreateItemRequest, UpdateItemRequest, ItemStatus, Responsible, ItemFile } from '../../models/SafetyPortalTypes';
import Spinner from '../../components/Spinner/Spinner';
import { FormErrorHandler } from '../../utils/FormErrorHandler';
import { ErrorTypeHandler } from '../../utils/ErrorTypeHandler';
import { ApiError } from '../../models/ApiError';
import CameraUpload from '../../components/CameraUpload/CameraUpload';
import { generateClientFileId } from '../../utils/fileUtils';
import styles from './CreateItem.module.css';

const CreateItem: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isEditMode = !!id;

  // Get today's date in YYYY-MM-DD format for max date restriction
  const today = new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    lastCheckDate: '',
    nextCheckDate: '',
    checkIntervalMonths: '',
    responsible: 'internal' as Responsible,
    notes: '',
    status: 'pending' as ItemStatus,
    model: '',
    licenseNumber: '',
    insuranceDate: '',
    licenseExpirationDate: '',
  });

  const [files, setFiles] = useState<File[]>([]);
  const [insuranceFile, setInsuranceFile] = useState<File | null>(null);
  const [insuranceFilePreview, setInsuranceFilePreview] = useState<string>('');
  const [existingInsuranceFile, setExistingInsuranceFile] = useState<ItemFile | null>(null);
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [licenseFilePreview, setLicenseFilePreview] = useState<string>('');
  const [existingLicenseFile, setExistingLicenseFile] = useState<ItemFile | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string>('');

  // Fetch item for edit mode
  const { data: existingItem, isLoading: isLoadingItem } = useQuery<Item | undefined>({
    queryKey: ['item', id],
    queryFn: async () => {
      if (!id) return undefined;
      try {
        return await getItem(id);
      } catch (error) {
        console.error('Error fetching item:', error);
        return undefined;
      }
    },
    enabled: isEditMode && !!id,
  });

  // Populate form when editing
  useEffect(() => {
    if (existingItem) {
      setFormData({
        name: existingItem.name,
        description: existingItem.description || '',
        location: existingItem.location || '',
        lastCheckDate: existingItem.lastCheckDate ? existingItem.lastCheckDate.split('T')[0] : '',
        nextCheckDate: existingItem.nextCheckDate ? existingItem.nextCheckDate.split('T')[0] : '',
        checkIntervalMonths: existingItem.checkIntervalMonths?.toString() || '',
        responsible: existingItem.responsible || 'internal',
        notes: existingItem.notes || '',
        status: existingItem.status,
        model: existingItem.model || '',
        licenseNumber: existingItem.licenseNumber || '',
        insuranceDate: existingItem.insuranceDate ? existingItem.insuranceDate.split('T')[0] : '',
        licenseExpirationDate: existingItem.licenseExpirationDate ? existingItem.licenseExpirationDate.split('T')[0] : '',
      });
      setExistingInsuranceFile(existingItem.insuranceFile || null);
      setExistingLicenseFile(existingItem.licenseFile || null);
    }
  }, [existingItem]);

  const uploadSingleFile = async (
    responseFile: ItemFile | null | undefined,
    localFile: File | null,
  ): Promise<{ success: boolean; fileName: string; error?: string }> => {
    if (!responseFile?.presignUrl || !localFile) return { success: true, fileName: localFile?.name || '' };
    try {
      const response = await fetch(responseFile.presignUrl, {
        method: 'PUT',
        headers: { 'Content-Type': responseFile.contentType },
        body: localFile,
      });
      if (!response.ok) throw new Error(`Upload failed with status ${response.status}`);
      return { success: true, fileName: localFile.name };
    } catch (error) {
      return { success: false, fileName: localFile.name, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  const uploadGeneralFiles = async (
    item: Item,
    fileMetadata: Array<{ fileName: string; fileExtension: string; contentType: string; clientFileId?: string }>,
    localFiles: File[],
  ): Promise<string[]> => {
    const failedUploads: string[] = [];
    if (localFiles.length === 0) return failedUploads;

    const uploadPromises = localFiles.map(async (file, index) => {
      const requestFile = fileMetadata[index];
      if (!requestFile || !requestFile.clientFileId) return { success: true, fileName: file.name };
      const responseFile = item.files?.find(f => f.clientFileId === requestFile.clientFileId);
      if (responseFile?.presignUrl) {
        try {
          const response = await fetch(responseFile.presignUrl, {
            method: 'PUT',
            headers: { 'Content-Type': responseFile.contentType },
            body: file,
          });
          if (!response.ok) throw new Error(`Upload failed with status ${response.status}`);
          return { success: true, fileName: file.name };
        } catch (error) {
          return { success: false, fileName: file.name, error: error instanceof Error ? error.message : 'Unknown error' };
        }
      }
      return { success: true, fileName: file.name };
    });

    const results = await Promise.allSettled(uploadPromises);
    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value && !result.value.success) {
        failedUploads.push(result.value.fileName);
      } else if (result.status === 'rejected') {
        failedUploads.push(localFiles[index]?.name || `File ${index + 1}`);
      }
    });
    return failedUploads;
  };

  const createMutation = useMutation({
    mutationFn: async (data: CreateItemRequest) => {
      const item = await createItem(data);
      const failedUploads: string[] = [];

      // Upload general files
      const generalFailed = await uploadGeneralFiles(item, data.files || [], files);
      failedUploads.push(...generalFailed);

      // Upload insurance file
      const insuranceResult = await uploadSingleFile(item.insuranceFile, insuranceFile);
      if (!insuranceResult.success) failedUploads.push(insuranceResult.fileName);

      // Upload license file
      const licenseResult = await uploadSingleFile(item.licenseFile, licenseFile);
      if (!licenseResult.success) failedUploads.push(licenseResult.fileName);

      if (failedUploads.length > 0) {
        const errorMessage = failedUploads.length === 1
          ? t('Items.Messages.FileUploadError', { fileName: failedUploads[0] }) || `Failed to upload file: ${failedUploads[0]}`
          : t('Items.Messages.MultipleFileUploadError', { count: failedUploads.length }) || `Failed to upload ${failedUploads.length} file(s): ${failedUploads.join(', ')}`;
        throw new Error(errorMessage);
      }
      
      return item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      navigate('/items');
    },
    onError: (error: any) => {
      handleError(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateItemRequest) => {
      const item = await updateItem(data);
      const failedUploads: string[] = [];

      // Upload general files
      const generalFailed = await uploadGeneralFiles(item, data.files || [], files);
      failedUploads.push(...generalFailed);

      // Upload insurance file
      const insuranceResult = await uploadSingleFile(item.insuranceFile, insuranceFile);
      if (!insuranceResult.success) failedUploads.push(insuranceResult.fileName);

      // Upload license file
      const licenseResult = await uploadSingleFile(item.licenseFile, licenseFile);
      if (!licenseResult.success) failedUploads.push(licenseResult.fileName);

      if (failedUploads.length > 0) {
        const errorMessage = failedUploads.length === 1
          ? t('Items.Messages.FileUploadError', { fileName: failedUploads[0] }) || `Failed to upload file: ${failedUploads[0]}`
          : t('Items.Messages.MultipleFileUploadError', { count: failedUploads.length }) || `Failed to upload ${failedUploads.length} file(s): ${failedUploads.join(', ')}`;
        throw new Error(errorMessage);
      }
      
      return item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      navigate('/items');
    },
    onError: (error: any) => {
      handleError(error);
    },
  });

  const handleError = (error: any) => {
    if (ApiError.isApiError(error)) {
      const fieldErrors = FormErrorHandler.extractFieldErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors(fieldErrors);
      } else {
        const errorMessage = ErrorTypeHandler.getUserFriendlyMessage(error);
        setGeneralError(errorMessage);
      }
    } else if (error instanceof Error && error.message) {
      // Handle regular Error objects (e.g., file upload errors)
      setGeneralError(error.message);
    } else {
      setGeneralError(t('Items.Messages.SaveError'));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
    setGeneralError('');
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
    setGeneralError('');
  };


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    setGeneralError('');

    // Validation
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) {
      errors.name = t('Items.Errors.NameRequired');
    }
    if (!isEditMode && !formData.checkIntervalMonths) {
      errors.checkIntervalMonths = t('Items.Errors.CheckIntervalMonthsRequired') || 'Check interval (months) is required';
    }
    if (!isEditMode && !formData.responsible) {
      errors.responsible = t('Items.Errors.ResponsibleRequired') || 'Responsible field is required';
    }
    if (formData.checkIntervalMonths && parseInt(formData.checkIntervalMonths, 10) <= 0) {
      errors.checkIntervalMonths = t('Items.Errors.CheckIntervalMonthsPositive') || 'Check interval must be a positive number';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Prepare file metadata with clientFileId
    const fileMetadata = files.map((file) => {
      const extension = file.name.split('.').pop() || '';
      return {
        fileName: file.name.replace(/\.[^/.]+$/, ''),
        fileExtension: extension,
        contentType: file.type || 'application/octet-stream',
        clientFileId: generateClientFileId(),
      };
    });

    // Prepare insurance file metadata
    const insuranceFileMeta = insuranceFile ? {
      fileName: insuranceFile.name.replace(/\.[^/.]+$/, ''),
      fileExtension: insuranceFile.name.split('.').pop() || '',
      contentType: insuranceFile.type || 'image/jpeg',
      clientFileId: generateClientFileId(),
    } : undefined;

    // Prepare license file metadata
    const licenseFileMeta = licenseFile ? {
      fileName: licenseFile.name.replace(/\.[^/.]+$/, ''),
      fileExtension: licenseFile.name.split('.').pop() || '',
      contentType: licenseFile.type || 'image/jpeg',
      clientFileId: generateClientFileId(),
    } : undefined;

    if (isEditMode && id) {
      const updateData: UpdateItemRequest = {
        _id: id,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        location: formData.location.trim() || undefined,
        lastCheckDate: formData.lastCheckDate || undefined,
        nextCheckDate: formData.nextCheckDate || undefined,
        checkIntervalMonths: formData.checkIntervalMonths ? parseInt(formData.checkIntervalMonths, 10) : undefined,
        responsible: formData.responsible,
        notes: formData.notes.trim() || undefined,
        status: formData.status,
        files: fileMetadata.length > 0 ? fileMetadata : undefined,
        model: formData.model.trim() || undefined,
        licenseNumber: formData.licenseNumber.trim() || undefined,
        insuranceDate: formData.insuranceDate ? new Date(formData.insuranceDate).toISOString() : (existingItem?.insuranceDate && !formData.insuranceDate ? null : undefined),
        licenseExpirationDate: formData.licenseExpirationDate ? new Date(formData.licenseExpirationDate).toISOString() : (existingItem?.licenseExpirationDate && !formData.licenseExpirationDate ? null : undefined),
        insuranceFile: insuranceFileMeta || (!existingInsuranceFile && existingItem?.insuranceFile ? null : undefined),
        licenseFile: licenseFileMeta || (!existingLicenseFile && existingItem?.licenseFile ? null : undefined),
      };
      updateMutation.mutate(updateData);
    } else {
      const createData: CreateItemRequest = {
        name: formData.name.trim(),
        checkIntervalMonths: parseInt(formData.checkIntervalMonths, 10),
        responsible: formData.responsible,
        description: formData.description.trim() || undefined,
        location: formData.location.trim() || undefined,
        lastCheckDate: formData.lastCheckDate || undefined,
        nextCheckDate: formData.nextCheckDate || undefined,
        notes: formData.notes.trim() || undefined,
        files: fileMetadata.length > 0 ? fileMetadata : undefined,
        model: formData.model.trim() || undefined,
        licenseNumber: formData.licenseNumber.trim() || undefined,
        insuranceDate: formData.insuranceDate ? new Date(formData.insuranceDate).toISOString() : undefined,
        licenseExpirationDate: formData.licenseExpirationDate ? new Date(formData.licenseExpirationDate).toISOString() : undefined,
        insuranceFile: insuranceFileMeta || undefined,
        licenseFile: licenseFileMeta || undefined,
      };
      createMutation.mutate(createData);
    }
  };

  if (isEditMode && isLoadingItem) return <Spinner />;

  return (
    <div className={styles.container}>
      <h2>{isEditMode ? t('Items.EditTitle') : t('Items.CreateTitle')}</h2>

      {(createMutation.isPending || updateMutation.isPending) && <Spinner />}

      <form onSubmit={handleSubmit}>
        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}

        <TextField
          fullWidth
          margin="normal"
          name="name"
          label={t('Items.Form.Name')}
          value={formData.name}
          onChange={handleInputChange}
          error={!!formErrors.name}
          helperText={formErrors.name}
          required
        />

        <TextField
          fullWidth
          margin="normal"
          name="description"
          label={t('Items.Form.Description')}
          value={formData.description}
          onChange={handleInputChange}
          error={!!formErrors.description}
          helperText={formErrors.description}
          multiline
          rows={3}
        />

        <TextField
          fullWidth
          margin="normal"
          name="location"
          label={t('Items.Form.Location')}
          value={formData.location}
          onChange={handleInputChange}
          error={!!formErrors.location}
          helperText={formErrors.location}
        />

        <TextField
          fullWidth
          margin="normal"
          name="lastCheckDate"
          label={t('Items.Form.LastCheckDate')}
          type="date"
          value={formData.lastCheckDate}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: today }}
          error={!!formErrors.lastCheckDate}
          helperText={formErrors.lastCheckDate}
        />

        <TextField
          fullWidth
          margin="normal"
          name="nextCheckDate"
          label={t('Items.Form.NextCheckDate')}
          type="date"
          value={formData.nextCheckDate}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: true }}
          error={!!formErrors.nextCheckDate}
          helperText={formErrors.nextCheckDate}
        />

        <TextField
          fullWidth
          margin="normal"
          name="checkIntervalMonths"
          label={t('Items.Form.CheckIntervalMonths')}
          type="number"
          value={formData.checkIntervalMonths}
          onChange={handleInputChange}
          inputProps={{ min: 1 }}
          required={!isEditMode}
          error={!!formErrors.checkIntervalMonths}
          helperText={formErrors.checkIntervalMonths || t('Items.Form.CheckIntervalMonthsHelp')}
        />

        <FormControl fullWidth margin="normal" required={!isEditMode}>
          <InputLabel>{t('Items.Form.Responsible')}</InputLabel>
          <Select
            name="responsible"
            value={formData.responsible}
            onChange={handleSelectChange}
            label={t('Items.Form.Responsible')}
            error={!!formErrors.responsible}
          >
            <MenuItem value="internal">{t('Items.Form.ResponsibleInternal') || 'פנימי'}</MenuItem>
            <MenuItem value="contractor">{t('Items.Form.ResponsibleContractor') || 'קבלן חיצוני'}</MenuItem>
          </Select>
          {formErrors.responsible && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {formErrors.responsible}
            </Typography>
          )}
        </FormControl>

        {isEditMode && (
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('Items.Form.Status')}</InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleSelectChange}
              label={t('Items.Form.Status')}
            >
              <MenuItem value="pending">{t('Items.Status.Pending')}</MenuItem>
              <MenuItem value="checked">{t('Items.Status.Checked')}</MenuItem>
              <MenuItem value="overdue">{t('Items.Status.Overdue')}</MenuItem>
            </Select>
          </FormControl>
        )}

        <TextField
          fullWidth
          margin="normal"
          name="notes"
          label={t('Items.Form.Notes')}
          value={formData.notes}
          onChange={handleInputChange}
          error={!!formErrors.notes}
          helperText={formErrors.notes}
          multiline
          rows={3}
        />

        <Divider sx={{ my: 3 }} />

        <TextField
          fullWidth
          margin="normal"
          name="model"
          label={t('Items.Form.Model')}
          value={formData.model}
          onChange={handleInputChange}
          error={!!formErrors.model}
          helperText={formErrors.model}
        />

        <TextField
          fullWidth
          margin="normal"
          name="licenseNumber"
          label={t('Items.Form.LicenseNumber')}
          value={formData.licenseNumber}
          onChange={handleInputChange}
          error={!!formErrors.licenseNumber}
          helperText={formErrors.licenseNumber}
        />

        <TextField
          fullWidth
          margin="normal"
          name="insuranceDate"
          label={t('Items.Form.InsuranceDate')}
          type="date"
          value={formData.insuranceDate}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: true }}
          error={!!formErrors.insuranceDate}
          helperText={formErrors.insuranceDate}
        />

        {/* Insurance file upload */}
        <Box mt={1} mb={2}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {t('Items.Form.InsuranceFile')}
          </Typography>
          {existingInsuranceFile && !insuranceFile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {existingInsuranceFile.downloadUrl && (
                <img
                  src={existingInsuranceFile.downloadUrl}
                  alt={t('Items.Form.InsuranceFile')}
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #e0e0e0' }}
                />
              )}
              <Chip
                label={existingInsuranceFile.baseFileName || existingInsuranceFile.fileName}
                size="small"
                onDelete={() => setExistingInsuranceFile(null)}
                deleteIcon={<DeleteIcon />}
              />
            </Box>
          )}
          {insuranceFilePreview && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <img
                src={insuranceFilePreview}
                alt="Insurance preview"
                style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #e0e0e0' }}
              />
              <Chip
                label={insuranceFile?.name}
                size="small"
                onDelete={() => {
                  setInsuranceFile(null);
                  URL.revokeObjectURL(insuranceFilePreview);
                  setInsuranceFilePreview('');
                }}
                deleteIcon={<DeleteIcon />}
              />
            </Box>
          )}
          {!insuranceFile && !existingInsuranceFile && (
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              component="label"
              sx={{
                backgroundColor: '#ffffff',
                borderColor: '#78B26E',
                color: '#333333',
                '&:hover': { backgroundColor: '#E1ECE2', borderColor: '#5E8856' },
              }}
            >
              {t('Items.Form.InsuranceFile')}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setInsuranceFile(file);
                    setInsuranceFilePreview(URL.createObjectURL(file));
                    setExistingInsuranceFile(null);
                  }
                  e.target.value = '';
                }}
              />
            </Button>
          )}
          {(existingInsuranceFile || insuranceFile) && (
            <Button
              variant="text"
              size="small"
              component="label"
              sx={{ mt: 0.5, color: '#78B26E' }}
            >
              {t('Items.Form.ChangeInsuranceFile')}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (insuranceFilePreview) URL.revokeObjectURL(insuranceFilePreview);
                    setInsuranceFile(file);
                    setInsuranceFilePreview(URL.createObjectURL(file));
                    setExistingInsuranceFile(null);
                  }
                  e.target.value = '';
                }}
              />
            </Button>
          )}
        </Box>

        <TextField
          fullWidth
          margin="normal"
          name="licenseExpirationDate"
          label={t('Items.Form.LicenseExpirationDate')}
          type="date"
          value={formData.licenseExpirationDate}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: true }}
          error={!!formErrors.licenseExpirationDate}
          helperText={formErrors.licenseExpirationDate}
        />

        {/* License file upload */}
        <Box mt={1} mb={2}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {t('Items.Form.LicenseFile')}
          </Typography>
          {existingLicenseFile && !licenseFile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {existingLicenseFile.downloadUrl && (
                <img
                  src={existingLicenseFile.downloadUrl}
                  alt={t('Items.Form.LicenseFile')}
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #e0e0e0' }}
                />
              )}
              <Chip
                label={existingLicenseFile.baseFileName || existingLicenseFile.fileName}
                size="small"
                onDelete={() => setExistingLicenseFile(null)}
                deleteIcon={<DeleteIcon />}
              />
            </Box>
          )}
          {licenseFilePreview && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <img
                src={licenseFilePreview}
                alt="License preview"
                style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #e0e0e0' }}
              />
              <Chip
                label={licenseFile?.name}
                size="small"
                onDelete={() => {
                  setLicenseFile(null);
                  URL.revokeObjectURL(licenseFilePreview);
                  setLicenseFilePreview('');
                }}
                deleteIcon={<DeleteIcon />}
              />
            </Box>
          )}
          {!licenseFile && !existingLicenseFile && (
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              component="label"
              sx={{
                backgroundColor: '#ffffff',
                borderColor: '#78B26E',
                color: '#333333',
                '&:hover': { backgroundColor: '#E1ECE2', borderColor: '#5E8856' },
              }}
            >
              {t('Items.Form.LicenseFile')}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setLicenseFile(file);
                    setLicenseFilePreview(URL.createObjectURL(file));
                    setExistingLicenseFile(null);
                  }
                  e.target.value = '';
                }}
              />
            </Button>
          )}
          {(existingLicenseFile || licenseFile) && (
            <Button
              variant="text"
              size="small"
              component="label"
              sx={{ mt: 0.5, color: '#78B26E' }}
            >
              {t('Items.Form.ChangeLicenseFile')}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (licenseFilePreview) URL.revokeObjectURL(licenseFilePreview);
                    setLicenseFile(file);
                    setLicenseFilePreview(URL.createObjectURL(file));
                    setExistingLicenseFile(null);
                  }
                  e.target.value = '';
                }}
              />
            </Button>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box mt={2} mb={2}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {t('Items.Form.Images')}
          </Typography>
          <CameraUpload onFilesSelected={setFiles} selectedFiles={files} />
        </Box>

        <Box mt={3} display="flex" gap={2}>
          <Button type="submit" variant="contained" disabled={createMutation.isPending || updateMutation.isPending}>
            {isEditMode ? t('General.Labels.Save') : t('General.Labels.Create')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/items')}
            sx={{
              backgroundColor: '#ffffff',
              borderColor: '#78B26E',
              color: '#333333',
              '&:hover': {
                backgroundColor: '#E1ECE2',
                borderColor: '#5E8856',
              },
            }}
          >
            {t('General.Labels.Cancel')}
          </Button>
        </Box>
      </form>
    </div>
  );
};

export default CreateItem;

